/**
 * useOrgUnitMutations Hook
 *
 * **역할:**
 * - 조직(OrgUnit) 생성, 수정, 삭제 기능을 제공하는 React Hook
 * - V29.9: Cloud Functions 경유로 전환 (직접 Firestore 쓰기 제거)
 * - 회사 스코프(companyCode) 검증은 서버에서 강제 적용
 *
 * **사용 예시:**
 * ```tsx
 * function OrgUnitManagementPage() {
 *   const {
 *     createOrgUnit,
 *     updateOrgUnit,
 *     deleteOrgUnit,
 *     isSubmitting,
 *     error
 *   } = useOrgUnitMutations();
 *
 *   const handleCreate = async (input: CreateOrgUnitInput) => {
 *     await createOrgUnit(input);
 *     // 성공 시 목록 새로고침 등
 *   };
 *
 *   return <button onClick={handleCreate}>조직 추가</button>;
 * }
 * ```
 */

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import {
  CreateOrgUnitInput,
  UpdateOrgUnitInput,
} from '../types/orgUnit';
import { useAuthContext } from './useAuthContext';
import { invalidateCache, CACHE_KEY } from '../utils/masterDataCache';

/**
 * useOrgUnitMutations Hook 반환 타입
 */
export interface UseOrgUnitMutationsResult {
  /**
   * 조직 생성 함수
   *
   * @param input 생성할 조직 정보
   * @throws Error 생성 실패 시
   *
   * **동작:**
   * 1. parentId가 있으면 부모 조직 조회
   * 2. level, path 자동 계산
   * 3. createdBy, updatedBy를 현재 사용자로 설정
   * 4. Firestore에 저장
   */
  createOrgUnit: (input: CreateOrgUnitInput) => Promise<string>;

  /**
   * 조직 수정 함수
   *
   * @param id 수정할 조직 ID
   * @param input 수정할 필드
   * @throws Error 수정 실패 시
   *
   * **동작:**
   * 1. 기존 문서 존재 여부 확인
   * 2. updatedBy, updatedAt 자동 설정
   * 3. Firestore 문서 업데이트
   */
  updateOrgUnit: (id: string, input: UpdateOrgUnitInput) => Promise<void>;

  /**
   * 조직 삭제/비활성화 함수 (DEPRECATED - deactivateOrgUnit 사용 권장)
   *
   * @param id 삭제할 조직 ID
   * @param force true면 실제 삭제, false면 비활성화 (기본값: false)
   * @throws Error 삭제 실패 시 (하위 조직이 있는 경우 등)
   *
   * **동작:**
   * 1. 하위 조직 존재 여부 확인
   * 2. force=false: status를 'archived'로 변경 (soft delete)
   * 3. force=true: 실제 문서 삭제 (하위 조직이 없을 때만)
   */
  deleteOrgUnit: (id: string, force?: boolean) => Promise<void>;

  /**
   * 조직 비활성화 함수 (Soft Delete)
   *
   * @param id 비활성화할 조직 ID
   * @throws Error 비활성화 실패 시
   *
   * **동작:**
   * 1. 하위 조직 존재 여부 확인
   * 2. status를 'archived'로 변경
   */
  deactivateOrgUnit: (id: string) => Promise<void>;

  /**
   * 조직 활성화 함수
   *
   * @param id 활성화할 조직 ID
   * @throws Error 활성화 실패 시
   *
   * **동작:**
   * 1. status를 'active'로 변경
   * 2. 조건 검증 없음 (옵션 A)
   */
  activateOrgUnit: (id: string) => Promise<void>;

  /**
   * 조직 완전 삭제 함수 (Hard Delete via Cloud Function)
   *
   * @param id 삭제할 조직 ID
   * @throws Error 삭제 실패 시 (조건 미충족 시 사유 포함)
   *
   * **동작:**
   * 1. Cloud Function 호출 (deleteOrgUnitSafely)
   * 2. 백엔드에서 무결성 검증:
   *    - 하위 조직 존재 여부
   *    - 소속 직원 존재 여부
   *    - 조직장/승인자로 지정 여부
   * 3. 조건 충족 시 Firestore에서 완전 삭제
   * 4. 조건 미충족 시 에러 발생 (사유 포함)
   */
  deleteOrgUnitHard: (id: string) => Promise<void>;

  /**
   * 제출(처리) 중 여부
   */
  isSubmitting: boolean;

  /**
   * 에러 정보
   */
  error: Error | null;
}

/**
 * V29.9: Cloud Functions 응답 타입
 */
interface CreateOrgUnitResponse {
  success: boolean;
  orgUnitId?: string;
  message?: string;
}

interface UpdateOrgUnitResponse {
  success: boolean;
  message?: string;
}

interface DeleteOrgUnitResponse {
  success: boolean;
  message?: string;
}

/**
 * useOrgUnitMutations Hook
 */
export function useOrgUnitMutations(): UseOrgUnitMutationsResult {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const authContext = useAuthContext();

  /**
   * 조직 생성 함수
   * V29.9: Cloud Function 경유로 전환
   * - 회사 스코프 검증은 서버에서 강제 적용
   * - companyCode는 parentId 기반으로 서버에서 자동 상속
   */
  const createOrgUnit = async (input: CreateOrgUnitInput): Promise<string> => {
    // Firebase Functions 미초기화 확인
    if (!functions) {
      throw new Error('Firebase Functions가 초기화되지 않았습니다.');
    }

    // 현재 사용자 확인
    if (!authContext.userId) {
      throw new Error('로그인이 필요합니다.');
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // V29.9: Cloud Function 호출
      const createOrgUnitCallable = httpsCallable<any, CreateOrgUnitResponse>(
        functions,
        'createOrgUnitCallable'
      );

      // 서버에 전달할 데이터 준비
      // 서버에서 level, path, companyCode를 자동 계산
      const requestData = {
        name: input.name,
        parentId: input.parentId || null,
        type: input.type,
        code: input.code || null,
        order: input.order,
        status: 'active',
        // 추가 필드 (서버에서 처리)
        isExternal: input.isExternal ?? false,
        externalCompanyName: input.externalCompanyName || null,
        description: input.description || null,
        approverUserId: input.approverUserId || null,
        // V26.1: type='company'인 경우에만 companyCode 전달
        ...(input.type === 'company' && (input as any).companyCode
          ? { companyCode: (input as any).companyCode }
          : {}),
      };

      const result = await createOrgUnitCallable(requestData);

      if (!result.data.success) {
        throw new Error(result.data.message || '조직 생성에 실패했습니다.');
      }

      const orgUnitId = result.data.orgUnitId!;
      // V259: 캐시 무효화
      invalidateCache(CACHE_KEY.ORG_UNITS);
      invalidateCache(CACHE_KEY.ORG_UNITS_PUBLIC);
      return orgUnitId;
    } catch (err: any) {
      console.error('[useOrgUnitMutations] V29.9 조직 생성 실패:', err);

      // Firebase Functions 에러 메시지 추출
      let errorMessage = '조직 생성 중 오류가 발생했습니다.';
      if (err && typeof err === 'object') {
        if (err.code === 'functions/permission-denied') {
          errorMessage = err.message || '조직 생성 권한이 없습니다.';
        } else if (err.code === 'functions/invalid-argument') {
          errorMessage = err.message || '입력값이 올바르지 않습니다.';
        } else if (err.code === 'functions/not-found') {
          errorMessage = err.message || '상위 조직을 찾을 수 없습니다.';
        } else if (err.message) {
          errorMessage = err.message;
        }
      }

      const errorObj = new Error(errorMessage);
      setError(errorObj);
      throw errorObj;
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * 조직 수정 함수
   * V29.9: Cloud Function 경유로 전환
   * - 회사 스코프 검증은 서버에서 강제 적용
   * - companyCode 변경 불가 (immutable)
   */
  const updateOrgUnit = async (id: string, input: UpdateOrgUnitInput): Promise<void> => {
    // Firebase Functions 미초기화 확인
    if (!functions) {
      throw new Error('Firebase Functions가 초기화되지 않았습니다.');
    }

    // 현재 사용자 확인
    if (!authContext.userId) {
      throw new Error('로그인이 필요합니다.');
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // V29.9: Cloud Function 호출
      const updateOrgUnitCallable = httpsCallable<any, UpdateOrgUnitResponse>(
        functions,
        'updateOrgUnitCallable'
      );

      // 서버에 전달할 데이터 준비
      const requestData: any = {
        orgUnitId: id,
      };

      // 변경된 필드만 전달
      if (input.name !== undefined) requestData.name = input.name;
      if (input.code !== undefined) requestData.code = input.code;
      if (input.parentId !== undefined) requestData.parentId = input.parentId || null;
      if (input.type !== undefined) requestData.type = input.type;
      if (input.status !== undefined) requestData.status = input.status;
      if (input.order !== undefined) requestData.order = input.order;
      if (input.description !== undefined) requestData.description = input.description;
      if (input.isExternal !== undefined) requestData.isExternal = input.isExternal;
      if (input.externalCompanyName !== undefined) requestData.externalCompanyName = input.externalCompanyName;
      if ('approverUserId' in input) requestData.approverUserId = input.approverUserId || null;
      // V75.2: 휴가 알림 DL 이메일
      if ('leaveNotificationDlEmails' in input) requestData.leaveNotificationDlEmails = input.leaveNotificationDlEmails;

      const result = await updateOrgUnitCallable(requestData);

      if (!result.data.success) {
        throw new Error(result.data.message || '조직 수정에 실패했습니다.');
      }

      // V259: 캐시 무효화
      invalidateCache(CACHE_KEY.ORG_UNITS);
      invalidateCache(CACHE_KEY.ORG_UNITS_PUBLIC);
    } catch (err: any) {
      console.error('[useOrgUnitMutations] V29.9 조직 수정 실패:', err);

      // Firebase Functions 에러 메시지 추출
      let errorMessage = '조직 수정 중 오류가 발생했습니다.';
      if (err && typeof err === 'object') {
        if (err.code === 'functions/permission-denied') {
          errorMessage = err.message || '조직 수정 권한이 없습니다.';
        } else if (err.code === 'functions/invalid-argument') {
          errorMessage = err.message || '입력값이 올바르지 않습니다.';
        } else if (err.code === 'functions/not-found') {
          errorMessage = err.message || '조직을 찾을 수 없습니다.';
        } else if (err.message) {
          errorMessage = err.message;
        }
      }

      const errorObj = new Error(errorMessage);
      setError(errorObj);
      throw errorObj;
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * 조직 완전 삭제 함수 (Cloud Function 호출)
   * V29.9: deleteOrgUnit보다 먼저 정의 (호출 순서상 필요)
   */
  const deleteOrgUnitHard = async (id: string): Promise<void> => {
    // Firebase Functions 미초기화 확인
    if (!functions) {
      throw new Error('Firebase Functions가 초기화되지 않았습니다.');
    }

    // 현재 사용자 확인
    if (!authContext.userId) {
      throw new Error('로그인이 필요합니다.');
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Cloud Function 호출
      const deleteOrgUnitSafely = httpsCallable<any, DeleteOrgUnitResponse>(
        functions,
        'deleteOrgUnitSafely'
      );
      const result = await deleteOrgUnitSafely({ orgUnitId: id });

      // V259: 캐시 무효화
      invalidateCache(CACHE_KEY.ORG_UNITS);
      invalidateCache(CACHE_KEY.ORG_UNITS_PUBLIC);
    } catch (err: any) {
      console.error('[useOrgUnitMutations] V29.9 조직 완전 삭제 실패:', err);

      // Firebase Functions 에러 메시지 추출
      let errorMessage = '조직 완전 삭제 중 오류가 발생했습니다.';

      if (err && typeof err === 'object') {
        if (err.code === 'functions/invalid-argument') {
          errorMessage = err.message || 'orgUnitId가 필요합니다.';
        } else if (err.code === 'functions/unauthenticated') {
          errorMessage = err.message || '로그인이 필요합니다.';
        } else if (err.code === 'functions/permission-denied') {
          errorMessage = err.message || '조직 삭제 권한이 없습니다.';
        } else if (err.code === 'functions/failed-precondition') {
          // 비즈니스 로직 검증 실패 메시지
          errorMessage = err.message || '조직을 삭제할 수 없습니다.';
        } else if (err.code === 'functions/internal') {
          errorMessage = err.message || '서버 내부 오류가 발생했습니다.';
        } else if (err.message) {
          errorMessage = err.message;
        }
      }

      const error = new Error(errorMessage);
      setError(error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * 조직 삭제/비활성화 함수
   * V29.9: Cloud Function 경유로 전환
   * - force=false: updateOrgUnitCallable로 status='archived' (소프트 삭제)
   * - force=true: deleteOrgUnitHard로 완전 삭제
   */
  const deleteOrgUnit = async (id: string, force: boolean = false): Promise<void> => {
    if (force) {
      // 완전 삭제는 deleteOrgUnitHard 호출
      await deleteOrgUnitHard(id);
      return;
    }

    // 소프트 삭제: status를 'archived'로 변경
    // Firebase Functions 미초기화 확인
    if (!functions) {
      throw new Error('Firebase Functions가 초기화되지 않았습니다.');
    }

    // 현재 사용자 확인
    if (!authContext.userId) {
      throw new Error('로그인이 필요합니다.');
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // V29.9: Cloud Function 호출 (updateOrgUnitCallable로 status 변경)
      const updateOrgUnitCallable = httpsCallable<any, UpdateOrgUnitResponse>(
        functions,
        'updateOrgUnitCallable'
      );

      const result = await updateOrgUnitCallable({
        orgUnitId: id,
        status: 'archived',
      });

      if (!result.data.success) {
        throw new Error(result.data.message || '조직 비활성화에 실패했습니다.');
      }

      // V259: 캐시 무효화
      invalidateCache(CACHE_KEY.ORG_UNITS);
      invalidateCache(CACHE_KEY.ORG_UNITS_PUBLIC);
    } catch (err: any) {
      console.error('[useOrgUnitMutations] V29.9 조직 비활성화 실패:', err);

      // Firebase Functions 에러 메시지 추출
      let errorMessage = '조직 비활성화 중 오류가 발생했습니다.';
      if (err && typeof err === 'object') {
        if (err.code === 'functions/permission-denied') {
          errorMessage = err.message || '조직 비활성화 권한이 없습니다.';
        } else if (err.code === 'functions/not-found') {
          errorMessage = err.message || '조직을 찾을 수 없습니다.';
        } else if (err.message) {
          errorMessage = err.message;
        }
      }

      const errorObj = new Error(errorMessage);
      setError(errorObj);
      throw errorObj;
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * 조직 비활성화 함수 (Soft Delete)
   */
  const deactivateOrgUnit = async (id: string): Promise<void> => {
    // 기존 deleteOrgUnit(id, false)와 동일
    await deleteOrgUnit(id, false);
  };

  /**
   * 조직 활성화 함수
   * V29.9: Cloud Function 경유로 전환
   * - updateOrgUnitCallable로 status='active'
   */
  const activateOrgUnit = async (id: string): Promise<void> => {
    // Firebase Functions 미초기화 확인
    if (!functions) {
      throw new Error('Firebase Functions가 초기화되지 않았습니다.');
    }

    // 현재 사용자 확인
    if (!authContext.userId) {
      throw new Error('로그인이 필요합니다.');
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // V29.9: Cloud Function 호출 (updateOrgUnitCallable로 status 변경)
      const updateOrgUnitCallable = httpsCallable<any, UpdateOrgUnitResponse>(
        functions,
        'updateOrgUnitCallable'
      );

      const result = await updateOrgUnitCallable({
        orgUnitId: id,
        status: 'active',
      });

      if (!result.data.success) {
        throw new Error(result.data.message || '조직 활성화에 실패했습니다.');
      }

      // V259: 캐시 무효화
      invalidateCache(CACHE_KEY.ORG_UNITS);
      invalidateCache(CACHE_KEY.ORG_UNITS_PUBLIC);
    } catch (err: any) {
      console.error('[useOrgUnitMutations] V29.9 조직 활성화 실패:', err);

      // Firebase Functions 에러 메시지 추출
      let errorMessage = '조직 활성화 중 오류가 발생했습니다.';
      if (err && typeof err === 'object') {
        if (err.code === 'functions/permission-denied') {
          errorMessage = err.message || '조직 활성화 권한이 없습니다.';
        } else if (err.code === 'functions/not-found') {
          errorMessage = err.message || '조직을 찾을 수 없습니다.';
        } else if (err.message) {
          errorMessage = err.message;
        }
      }

      const errorObj = new Error(errorMessage);
      setError(errorObj);
      throw errorObj;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    createOrgUnit,
    updateOrgUnit,
    deleteOrgUnit,
    deactivateOrgUnit,
    activateOrgUnit,
    deleteOrgUnitHard,
    isSubmitting,
    error,
  };
}
